import {
  FaCheckCircle,
  FaClipboardCheck,
  FaFileInvoiceDollar,
  FaShieldAlt,
  FaStethoscope,
  FaUserCheck,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";
import ServiceCTASection from "../../components/ServiceCTASection";

const seo = {
  title: "Medical Billing Consulting Services | Physicians Success Partners",
  description:
    "PSP's expert medical billing consultants help reduce claim denials, accelerate reimbursements, and optimize your clinic's full revenue cycle.",
  keywords:
    "medical billing service, medical coding service, outsourced billing, claims management",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Medical Billing Services",
      url: "https://psprcm.us/services/medical-billing/",
      description:
        "PSP's expert medical billing consultants help reduce claim denials, accelerate reimbursements, and optimize your clinic's full revenue cycle.",
      serviceType: "Medical Billing",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Medical Billing Solutions",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Charge Entry" },
          },
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Claims Submission" },
          },
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Payment Posting" },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Accounts Receivable (AR) Follow-Up",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Denial Management & Appeals",
            },
          },
        ],
      },
    },
    createBreadcrumbSchema(
      "/services/medical-billing",
      "Medical Billing & Coding Services",
    ),
  ],
};

export default function MedicalBilling() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/medical-billing"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
          heading: "Medical Billing Consulting Service",
          paragraph:
            "Physicians Success Partners (PSP) provides expert medical billing consulting, partnering closely with healthcare providers to reduce denials, improve processes, and accelerate reimbursements.",
          image: "/img/services-img/medical-billing.webp",
        }}
        grid={{
          kicker: "Why Choose Us",
          heading: "Medical Billing Consulting That Helps You Get Paid Faster",
          description:
            "At Physicians Success Partners (PSP), our consulting approach is built around delivering consistent, on-time reimbursements for healthcare providers. Our team of certified billing specialists brings deep expertise across multiple specialties including primary care, cardiology, orthopedics, and mental health. We identify revenue leakage, fix coding errors, and build efficient billing workflows tailored to your practice, so you get paid faster, with fewer denials and less administrative burden.",
          metrics: [
            {
              icon: <FaUserCheck />,
              value: "20+",
              label: "Years of Combined Industry Experience",
              style: "metricPrimary",
            },
            {
              icon: <FaFileInvoiceDollar />,
              value: "03%",
              label: "AR Rejection Rate",
              style: "metricAccent",
            },
            {
              icon: <FaClipboardCheck />,
              value: "98%",
              label: "Accuracy Rate",
              style: "metricLight",
            },
            {
              icon: <FaShieldAlt />,
              value: "100%",
              label: "HIPAA Compliance",
              style: "metricDark",
            },
          ],
          panelEyebrow: "Operational Confidence",
          panelHeading:
            "Optimized for Consistent and Accurate Medical Billing Outcomes at Every Stage",
          assurances: [
            { icon: <FaCheckCircle />, label: "Accurate Procedure Review" },
            { icon: <FaStethoscope />, label: "Reliable Diagnosis Support" },
            { icon: <FaFileInvoiceDollar />, label: "Timely Payments" },
          ],
        }}
        services={{
          heading:
            "PSP RCM's Top Rated Billing Consultancy Group Is Here for Medical Billing Help",
          description:
            "PSP's medical billing consultants analyze your entire revenue cycle from patient registration to final payment. We identify bottlenecks, reduce claim rejections, and implement best practices that ensure accurate billing at every stage. Our structured approach helps practices of all sizes achieve sustainable revenue growth while staying fully HIPAA compliant.",
          items: [
            {
              title: "Cleaner Claim Submission",
              text: "We organize billing and coding workflows carefully so claims go out with stronger accuracy, fewer preventable errors, and better first-pass outcomes.",
            },
            {
              title: "Steadier Revenue Performance",
              text: "Our team supports payment consistency with reliable follow-up, reimbursement visibility, and practical billing oversight for everyday operations.",
            },
            {
              title: "Reduced Administrative Burden",
              text: "By handling complex billing tasks externally, we help your practice save time and keep internal teams focused on patient-facing priorities.",
            },
            {
              title: "Compliance-Minded Execution",
              text: "We stay aligned with coding and documentation expectations so your billing process remains dependable, organized, and easier to manage.",
            },
          ],
        }}
        faq={{
          image: "/img/services-img/medical-billing.webp",
          imageAlt: "Medical billing and coding support",
          title:
            "Hire a Dedicated Medical Billing Consultant to Optimize Your Clinic’s Revenue Cycle",
          description:
            "Our billing teams are more than just billers. We are every USA provider's best-managed billing partner. Our medical billing advocates partner with physicians to improve their practice management and achieve sustainable growth. Here is how Physicians Success Partners (PSP) healthcare billing consultancy group is helping practices with patient billing:",
        }}
        specialtiesContent={{
          eyebrow: "Medical Specialties",
          heading:
            "Specialty support tailored to the needs of modern practices.",
          description:
            "Explore the specialties we support through dependable billing workflows, organized documentation support, and professional revenue cycle execution.",
        }}
      />
      <ServiceCTASection
        imageSrc="/img/services-img/cta-section-img.webp"
        title="Dedicated Billing Consultant for Your Clinic’s RCM"
        description="Medical billing can be prone to errors, delays, and inefficiencies that affect your cash flow and profitability. PSP RCM's billing associates streamline your clinic’s billing process by taking care of the entire RCM with real-time reports and analytics."
      />
    </>
  );
}
